import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import Controls from './controls';
import { useMultistepMachine } from './use-multistep-machine';
import { useGiftboxImage } from './use-giftbox-image';
import { submitGiftbox, submitComplimentaryMessage } from './submit-giftbox';
import { buildSteps } from './steps-config';
import { initialContext } from '../types';
import { t } from '../../theme/locales';
export const MultistepForm = ({ targetEl , giftboxProducts =null , giftboxDefaultImage =null , giftboxDefaultImageAlt =null , diyGiftboxProducts =null  })=>{
    var _steps_index;
    const { index , serviceRef , contextState , setContextState  } = useMultistepMachine();
    const [saving, setSaving] = useState(false);
    const contentRef = useRef(null);
    useEffect(()=>{
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [
        index
    ]);
    const [selectedProd, setSelectedProd] = useState(null);
    const { src: giftboxImageSrc , srcset: giftboxSrcset , alt: giftboxAlt  } = useGiftboxImage({
        giftboxKey: contextState.giftbox,
        giftboxProducts,
        diyGiftboxProducts,
        defaultImage: giftboxDefaultImage,
        defaultAlt: giftboxDefaultImageAlt !== null && giftboxDefaultImageAlt !== void 0 ? giftboxDefaultImageAlt : ''
    });
    // Keep selectedProd in sync for analytics
    useEffect(()=>{
        try {
            const prod = giftboxProducts && giftboxProducts[contextState.giftbox] || diyGiftboxProducts && diyGiftboxProducts[contextState.giftbox] || null;
            setSelectedProd(prod !== null && prod !== void 0 ? prod : null);
        } catch  {
            setSelectedProd(null);
        }
    }, [
        contextState.giftbox,
        giftboxProducts,
        diyGiftboxProducts
    ]);
    const steps = buildSteps({
        giftboxProducts,
        diyGiftboxProducts,
        giftboxGroup: contextState.giftboxGroup
    });
    const applySet = (newState)=>{
        const normalized = {
            ...newState || {}
        };
        if (Object.prototype.hasOwnProperty.call(newState || {}, 'items')) {
            const raw = newState.items;
            normalized.items = Array.isArray(raw) ? raw : raw ? [
                String(raw)
            ] : [];
        }
        setContextState((prev)=>({
                ...prev,
                ...normalized || {}
            }));
    };
    const handleSubmit = async ()=>{
        setSaving(true);
        try {
            await submitGiftbox({
                contextState,
                giftboxProducts,
                diyGiftboxProducts,
                selectedProd,
                onSuccess: ()=>{
                    var _serviceRef_current, _serviceRef_current1;
                    setContextState(initialContext);
                    (_serviceRef_current = serviceRef.current) === null || _serviceRef_current === void 0 ? void 0 : _serviceRef_current.send('BACK');
                    (_serviceRef_current1 = serviceRef.current) === null || _serviceRef_current1 === void 0 ? void 0 : _serviceRef_current1.send('BACK');
                    setSaving(false);
                }
            });
        } catch (err) {
            window.dispatchEvent(new CustomEvent('gift-wrapping:error', {
                detail: {
                    message: 'Unexpected error',
                    error: err
                }
            }));
            setSaving(false);
        }
    };
    const handleSubmitComplimentaryMessage = async ()=>{
        setSaving(true);
        try {
            var _contextState_complimentaryMessage;
            await submitComplimentaryMessage({
                message: (_contextState_complimentaryMessage = contextState.complimentaryMessage) !== null && _contextState_complimentaryMessage !== void 0 ? _contextState_complimentaryMessage : '',
                onSuccess: ()=>{
                    var _serviceRef_current;
                    setContextState(initialContext);
                    (_serviceRef_current = serviceRef.current) === null || _serviceRef_current === void 0 ? void 0 : _serviceRef_current.send('BACK');
                    setSaving(false);
                }
            });
        } catch (err) {
            window.dispatchEvent(new CustomEvent('gift-wrapping:error', {
                detail: {
                    message: 'Unexpected error',
                    error: err
                }
            }));
            setSaving(false);
        }
    };
    const goNext = ()=>{
        const step = steps[index];
        if (!step || !serviceRef.current) return;
        if (step.id === 'choose' && contextState.isDiy) {
            handleSubmit();
            return;
        }
        if (step.id === 'complimentary-message') {
            handleSubmitComplimentaryMessage();
            return;
        }
        if (step.id === 'select' && Array.isArray(contextState.items) && contextState.items.length > 0) {
            handleSubmit();
            return;
        }
        if (index < steps.length - 1) serviceRef.current.send('NEXT');
        else handleSubmit();
    };
    if (targetEl) {
        targetEl.innerHTML = `
      <div class="preview">
        Giftbox: ${contextState.giftbox || '—'}<br>
        Items: ${contextState.items || '—'}
        <img src="${giftboxImageSrc !== null && giftboxImageSrc !== void 0 ? giftboxImageSrc : ''}" alt="Giftbox preview" style="max-width:120px;display:block;margin-top:8px">
      </div>
    `;
    }
    const step = steps[index];
    const nextDisabled = !!(step === null || step === void 0 ? void 0 : step.validate) && !step.validate(contextState) || saving;
    const nextLabel = (step === null || step === void 0 ? void 0 : step.id) === 'complimentary-message' ? t('gift_wrapping.steps.controls.save') : (step === null || step === void 0 ? void 0 : step.id) === 'choose' && contextState.isDiy ? t('gift_wrapping.steps.controls.add_gift_to_bag') : (step === null || step === void 0 ? void 0 : step.id) === 'select' ? t('gift_wrapping.steps.controls.add_to_bag') : t('gift_wrapping.steps.controls.continue');
    return h('div', {
        className: 'gift-wrapping'
    }, [
        h('div', {
            className: 'gift-wrapping__target'
        }, [
            h('div', {
                className: 'gift-wrapping__background'
            }),
            h('div', {
                className: 'gift-wrapping__image-container'
            }, [
                h('img', {
                    src: giftboxImageSrc !== null && giftboxImageSrc !== void 0 ? giftboxImageSrc : '',
                    srcset: giftboxSrcset,
                    sizes: '(min-width: 64em) 500px, 300px',
                    alt: giftboxAlt,
                    className: 'gift-wrapping__image',
                    decoding: 'async'
                })
            ])
        ]),
        h('div', {
            className: 'gift-wrapping__content'
        }, [
            h('div', {
                className: 'gift-wrapping__content__inner',
                ref: contentRef
            }, [
                h('h3', {
                    className: 'gift-wrapping__step__title',
                    'data-testid': 'gift-wrapping-step-title'
                }, ((_steps_index = steps[index]) === null || _steps_index === void 0 ? void 0 : _steps_index.title) || ''),
                step ? step.render(contextState, applySet) : null
            ]),
            h(Controls, {
                index,
                stepsCount: steps.length,
                nextDisabled,
                nextLabel,
                saving,
                onBack: ()=>{
                    var _serviceRef_current;
                    return (_serviceRef_current = serviceRef.current) === null || _serviceRef_current === void 0 ? void 0 : _serviceRef_current.send('BACK');
                },
                onNext: goNext
            })
        ])
    ]);
};
